import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

export type Theme = 'dark' | 'light'

/** 'system' follows the OS; 'dark'/'light' pin it. */
export type ThemePreference = Theme | 'system'

interface ThemeContextValue {
  /** The theme actually being rendered, after resolving 'system'. */
  theme: Theme
  preference: ThemePreference
  setPreference: (p: ThemePreference) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

const STORAGE_KEY = 'bloo.theme'

function systemTheme(): Theme {
  if (typeof window === 'undefined') return 'dark'
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

function storedPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'system'
  const raw = window.localStorage.getItem(STORAGE_KEY)
  return raw === 'dark' || raw === 'light' || raw === 'system' ? raw : 'system'
}

export interface ThemeProviderProps {
  children: ReactNode
  /** Override the initial preference. Mainly for tests and Storybook. */
  defaultPreference?: ThemePreference
}

/**
 * Sets `data-bloo-theme` on <html>, which is what the design system's CSS keys
 * off. The dark palette is the default because the product is dark-first.
 */
export function ThemeProvider({ children, defaultPreference }: ThemeProviderProps) {
  const [preference, setPreferenceState] = useState<ThemePreference>(
    () => defaultPreference ?? storedPreference(),
  )
  const [system, setSystem] = useState<Theme>(systemTheme)

  // Track OS changes so 'system' stays live rather than sampling once at mount.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: light)')
    const onChange = () => setSystem(mq.matches ? 'light' : 'dark')
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const theme: Theme = preference === 'system' ? system : preference

  useEffect(() => {
    document.documentElement.setAttribute('data-bloo-theme', theme)
    document.documentElement.style.colorScheme = theme
  }, [theme])

  const setPreference = useCallback((p: ThemePreference) => {
    setPreferenceState(p)
    window.localStorage.setItem(STORAGE_KEY, p)
  }, [])

  const value = useMemo(
    () => ({ theme, preference, setPreference }),
    [theme, preference, setPreference],
  )

  return <ThemeContext value={value}>{children}</ThemeContext>
}

export function useTheme(): ThemeContextValue {
  const ctx = use(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>')
  return ctx
}
